import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box, Button, Paper, Stack, TextField, Typography, MenuItem,
  Table, TableBody, TableCell, TableHead, TableRow, IconButton, Tooltip, Chip, LinearProgress,
  Backdrop, CircularProgress,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import DescriptionIcon from "@mui/icons-material/Description";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import TableViewIcon from "@mui/icons-material/TableView";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import SendIcon from "@mui/icons-material/Send";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import Swal from "sweetalert2";
import {
  listarFichas, excluirFicha, exportarFichas, notificarIncompleta, enviarParaAnalise,
  reanalisarDocumentos,
} from "../../services/fichaService";
import { listarEdicoes } from "../../services/edicaoService";

/**
 * Quanto da ficha o candidato já preencheu, em blocos (seções do questionário +
 * responsável, composição familiar e documentos). Serve para separar a ficha pronta
 * que ainda não foi enviada daquela que tem só o cadastro inicial.
 */
function Preenchimento({ f }) {
  const pct = Number(f.Percentual || 0);
  const cor = pct >= 100 ? "success" : pct >= 50 ? "primary" : pct > 0 ? "warning" : "error";
  const faltando = f.Faltando || [];
  const detalhe = !f.BlocosTotal
    ? "Sem dados"
    : faltando.length === 0
      ? `Tudo preenchido (${f.BlocosTotal} etapas)`
      : `${f.BlocosPreenchidos} de ${f.BlocosTotal} etapas • falta: ${faltando.join(", ")}`;
  return (
    <Tooltip title={detalhe}>
      <Stack spacing={0.3} sx={{ minWidth: 110 }}>
        <Stack direction="row" justifyContent="space-between">
          <Typography variant="caption" color="text.secondary">
            {pct === 0 ? "Só cadastro" : `${pct}%`}
          </Typography>
          {pct >= 100 && <Typography variant="caption" color="success.main">completa</Typography>}
        </Stack>
        <LinearProgress variant="determinate" value={Math.min(pct, 100)} color={cor}
          sx={{ height: 6, borderRadius: 3 }} />
      </Stack>
    </Tooltip>
  );
}

const moeda = (v) =>
  v == null ? "-" : Number(v).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

export default function FichaList() {
  const navigate = useNavigate();
  const [fichas, setFichas] = useState([]);
  const [termo, setTermo] = useState("");
  const [filtro, setFiltro] = useState("todas"); // todas | andamento | enviadas
  const [entrega, setEntrega] = useState("todas"); // todas | 1 Pendente | 2 Parcial | 3 Completa
  const [edicao, setEdicao] = useState("todas");
  const [documentos, setDocumentos] = useState("todas"); // todas | recusados | divergencias | atencao
  const [carregando, setCarregando] = useState(false);
  const [edicoes, setEdicoes] = useState([]);

  const carregar = async () => {
    setCarregando(true);
    try {
      const { data } = await listarFichas({
        termo,
        idEdicao: edicao === "todas" ? null : edicao,
        documentos: documentos === "todas" ? null : documentos,
      });
      setFichas(data);
    } catch (e) {
      Swal.fire("Erro", e?.response?.data?.mensagem || e?.response?.data || e.message, "error");
    } finally {
      setCarregando(false);
    }
  };

  useEffect(() => {
    carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [edicao, documentos]);

  useEffect(() => { listarEdicoes().then((r) => setEdicoes(r.data || [])).catch(() => {}); }, []);

  // As exportações recebem os mesmos filtros da grid, para o arquivo bater com a tela.
  const [exportando, setExportando] = useState(false);
  const exportar = async (formato) => {
    setExportando(true);
    try {
      await exportarFichas(formato, {
        termo, grupo: filtro, entrega,
        idEdicao: edicao === "todas" ? null : edicao,
        documentos: documentos === "todas" ? null : documentos,
      });
    }
    catch (e) { Swal.fire("Erro", e?.response?.data?.mensagem || e.message, "error"); }
    finally { setExportando(false); }
  };

  // Lembrete de ficha incompleta: só para quem ainda está preenchendo e não chegou a 100%.
  const podeLembrar = (f) =>
    (f.IdSituacaoInscricao == null || f.IdSituacaoInscricao === 1) && Number(f.Percentual || 0) < 100;

  // Ficha completa parada em preenchimento: a secretaria pode enviar sem depender do candidato.
  const podeEnviar = (f) =>
    (f.IdSituacaoInscricao == null || f.IdSituacaoInscricao === 1) && Number(f.Percentual || 0) >= 100;

  const [enviando, setEnviando] = useState(0);
  const enviarAnalise = async (f) => {
    const r = await Swal.fire({
      title: "Enviar para análise?",
      html: `A ficha <b>nº ${f.Id}</b> de <b>${f.Candidato || f.Responsavel || "—"}</b> está completa`
        + ` e será enviada para análise <b>em nome da secretaria</b>.`
        + `<br/><br/>O registro de quem enviou fica no parecer da ficha.`,
      icon: "question", showCancelButton: true, confirmButtonText: "Enviar", cancelButtonText: "Cancelar",
    });
    if (!r.isConfirmed) return;
    setEnviando(f.Id);
    try {
      const { data } = await enviarParaAnalise(f.Id);
      Swal.fire("Enviada", data.mensagem, "success");
      carregar();
    } catch (e) {
      const d = e?.response?.data;
      // O backend devolve o que falta; sem isso a secretaria não saberia o que cobrar.
      const itens = [].concat(d?.campos || [], d?.documentos || []);
      Swal.fire({
        icon: "warning", title: "Não foi possível enviar",
        html: (d?.mensagem || e.message) + (itens.length ? `<br/><br/>• ${itens.join("<br/>• ")}` : ""),
      });
    } finally { setEnviando(0); }
  };

  const [notificando, setNotificando] = useState(0);
  const lembrar = async (f) => {
    const r = await Swal.fire({
      title: "Enviar lembrete?",
      html: `A ficha <b>nº ${f.Id}</b> está <b>${Number(f.Percentual || 0)}%</b> preenchida.`
        + ((f.Faltando || []).length
            ? `<br/><br/>Falta: <i>${f.Faltando.join(", ")}</i>`
            : "")
        + `<br/><br/>Será enviado um WhatsApp (e e-mail, se configurado) para ${f.Candidato || f.Responsavel || "o candidato"}.`,
      icon: "question", showCancelButton: true, confirmButtonText: "Enviar", cancelButtonText: "Cancelar",
    });
    if (!r.isConfirmed) return;
    setNotificando(f.Id);
    try {
      const { data } = await notificarIncompleta(f.Id);
      Swal.fire(data.whatsapp ? "Enviado" : "Atenção", data.mensagem, data.whatsapp ? "success" : "warning");
    } catch (e) {
      Swal.fire("Erro", e?.response?.data?.mensagem || e?.response?.data || e.message, "error");
    } finally { setNotificando(0); }
  };

  // Conferir os documentos de todas as fichas filtradas, sem abrir uma a uma.
  const [validando, setValidando] = useState(false);
  const validarDocumentos = async () => {
    const r = await Swal.fire({
      title: "Validar documentos?",
      html: `Serão conferidas as <b>${fichasFiltradas.length}</b> ficha(s) dos filtros atuais.`
        + "<br/><br/>A conferência procura arquivo repetido em tipos diferentes, PDF incompleto,"
        + " formato incorreto e arquivo ilegível. Pode levar alguns minutos.",
      icon: "question", showCancelButton: true, confirmButtonText: "Validar", cancelButtonText: "Cancelar",
    });
    if (!r.isConfirmed) return;
    setValidando(true);
    try {
      const { data } = await reanalisarDocumentos({
        termo, grupo: filtro, entrega,
        idEdicao: edicao === "todas" ? null : edicao,
        documentos: documentos === "todas" ? null : documentos,
      });
      await Swal.fire({
        icon: data.fichasComProblema ? "warning" : "success",
        title: "Conferência concluída",
        html: data.mensagem + (data.truncado
          ? "<br/><br/><small>Havia mais fichas que o limite por execução — refine os filtros e repita.</small>"
          : ""),
      });
      carregar();
    } catch (e) { Swal.fire("Erro", e?.response?.data?.mensagem || e.message, "error"); }
    finally { setValidando(false); }
  };

  const excluir = async (id) => {
    const r = await Swal.fire({
      title: "Excluir ficha?",
      text: "Esta ação não pode ser desfeita.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Excluir",
      cancelButtonText: "Cancelar",
    });
    if (!r.isConfirmed) return;
    await excluirFicha(id);
    carregar();
  };

  // Ficha só pode ser excluída enquanto está em preenchimento (1) — depois de enviada
  // ela é o registro da inscrição, com anexos e análise.
  const podeExcluir = (f) => f.IdSituacaoInscricao == null || f.IdSituacaoInscricao === 1;

  // Em andamento = Em preenchimento (1); Enviadas/concluídas = Enviada em diante (>=2).
  const fichasFiltradas = fichas.filter((f) => {
    if (entrega !== "todas" && String(f.IdSituacaoEntrega ?? "") !== String(entrega)) return false;
    if (filtro === "andamento") return f.IdSituacaoInscricao === 1;
    if (filtro === "enviadas") return f.IdSituacaoInscricao >= 2;
    return true;
  });

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5">Fichas Socioeconômicas</Typography>
        <Stack direction="row" spacing={1}>
          <Button variant="contained" onClick={() => navigate("/fichas/nova")}>Nova ficha</Button>
        </Stack>
      </Stack>

      <Stack direction="row" spacing={1} mb={2} flexWrap="wrap" useFlexGap>
        <TextField
          size="small"
          label="Buscar por nome, CPF, quem cadastrou, nº inscrito ou ID"
          value={termo}
          onChange={(e) => setTermo(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && carregar()}
          sx={{ width: 360 }}
        />
        <Button variant="outlined" onClick={carregar}>Buscar</Button>
        <TextField select size="small" label="Situação" value={filtro}
          onChange={(e) => setFiltro(e.target.value)} sx={{ width: 220 }}>
          <MenuItem value="todas">Todas</MenuItem>
          <MenuItem value="andamento">Em andamento</MenuItem>
          <MenuItem value="enviadas">Enviadas / concluídas</MenuItem>
        </TextField>
        <TextField select size="small" label="Edição" value={edicao}
          onChange={(e) => setEdicao(e.target.value)} sx={{ width: 240 }}>
          <MenuItem value="todas">Todas</MenuItem>
          {edicoes.map((ed) => <MenuItem key={ed.Id} value={ed.Id}>{ed.Nome}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="Documentos" value={documentos}
          onChange={(e) => setDocumentos(e.target.value)} sx={{ width: 230 }}>
          <MenuItem value="todas">Todos</MenuItem>
          <MenuItem value="recusados">Com documento indeferido</MenuItem>
          <MenuItem value="divergencias">Com divergência na conferência</MenuItem>
          <MenuItem value="atencao">Indeferido ou divergência</MenuItem>
        </TextField>
        <TextField select size="small" label="Entrega de documentos" value={entrega}
          onChange={(e) => setEntrega(e.target.value)} sx={{ width: 220 }}>
          <MenuItem value="todas">Todas</MenuItem>
          <MenuItem value="1">Pendente</MenuItem>
          <MenuItem value="2">Parcial</MenuItem>
          <MenuItem value="3">Completa</MenuItem>
        </TextField>
        <Box sx={{ flex: 1 }} />
        <Chip label={`${fichasFiltradas.length} ficha(s)`} color="primary" variant="outlined" />
        <Button size="small" variant="outlined" startIcon={<FactCheckIcon />}
          disabled={validando || carregando} onClick={validarDocumentos}>Validar documentos</Button>
        <Button size="small" variant="outlined" startIcon={<PictureAsPdfIcon />}
          disabled={exportando} onClick={() => exportar("pdf")}>PDF</Button>
        <Button size="small" variant="outlined" startIcon={<TableViewIcon />}
          disabled={exportando} onClick={() => exportar("excel")}>Excel</Button>
      </Stack>

      <Paper sx={{ position: "relative" }}>
        {/* Sem isto a tela ficava parada e sem sinal de vida durante a carga. */}
        {carregando && <LinearProgress />}
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Edição</TableCell>
              <TableCell>Responsável</TableCell>
              <TableCell>Situação</TableCell>
              <TableCell>Entrega</TableCell>
              <TableCell sx={{ width: 150 }}>Preenchimento</TableCell>
              <TableCell align="right">Receitas</TableCell>
              <TableCell align="right">Despesas</TableCell>
              <TableCell align="right">Renda per capita</TableCell>
              <TableCell align="center">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {fichasFiltradas.map((f) => (
              <TableRow key={f.Id} hover>
                <TableCell>{f.Id}</TableCell>
                <TableCell>{f.Edicao}</TableCell>
                <TableCell>{f.Responsavel}</TableCell>
                <TableCell>{f.Situacao}</TableCell>
                <TableCell>
                  {f.SituacaoEntrega || "—"}
                  {f.QtdRecusados > 0 && (
                    <Tooltip title={`${f.QtdRecusados} documento(s) indeferido(s)`}>
                      <Chip size="small" color="error" variant="outlined" label={`${f.QtdRecusados} indef.`} sx={{ ml: 0.5 }} />
                    </Tooltip>
                  )}
                  {f.QtdDivergencias > 0 && (
                    <Tooltip title={`${f.QtdDivergencias} documento(s) com divergência na conferência automática`}>
                      <Chip size="small" color="warning" variant="outlined" label={`${f.QtdDivergencias} diverg.`} sx={{ ml: 0.5 }} />
                    </Tooltip>
                  )}
                </TableCell>
                <TableCell><Preenchimento f={f} /></TableCell>
                <TableCell align="right">{moeda(f.TotalReceitas)}</TableCell>
                <TableCell align="right">{moeda(f.TotalDespesas)}</TableCell>
                <TableCell align="right">{moeda(f.RendaPerCapita)}</TableCell>
                <TableCell align="center">
                  <IconButton size="small" title="Relatório" onClick={() => window.open(`/fichas/${f.Id}/relatorio`, "_blank")}>
                    <DescriptionIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" title="Editar" onClick={() => navigate(`/fichas/${f.Id}`)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                  {podeEnviar(f) && (
                    <Tooltip title="Enviar esta ficha para análise (sem depender do candidato)">
                      <span>
                        <IconButton size="small" color="primary" disabled={enviando === f.Id}
                          onClick={() => enviarAnalise(f)}>
                          <SendIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                  {podeLembrar(f) && (
                    <Tooltip title="Avisar o candidato que a ficha está incompleta">
                      <span>
                        <IconButton size="small" color="success" disabled={notificando === f.Id}
                          onClick={() => lembrar(f)}>
                          <WhatsAppIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                  {podeExcluir(f) ? (
                    <IconButton size="small" color="error" title="Excluir" onClick={() => excluir(f.Id)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  ) : (
                    <Tooltip title="Ficha já enviada não pode ser excluída">
                      <span>
                        <IconButton size="small" disabled><DeleteIcon fontSize="small" /></IconButton>
                      </span>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {fichasFiltradas.length === 0 && (
              <TableRow>
                <TableCell colSpan={10} align="center">
                  {carregando ? "Carregando fichas…" : "Nenhuma ficha encontrada."}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      <Backdrop open={validando} sx={{ zIndex: (t) => t.zIndex.drawer + 2, color: "#fff" }}>
        <Stack alignItems="center" spacing={2}>
          <CircularProgress color="inherit" />
          <Typography>Conferindo os documentos… aguarde.</Typography>
        </Stack>
      </Backdrop>
    </Box>
  );
}
